import React, { useState, useEffect } from 'react';
import axios from 'axios';
const API = '/api';

interface Offer {
  id: string;
  account_id: string;
  account_name: string;
  name: string;
  sku: string;
  price: number;
  currency: string;
  stock_available: number;
  image_url: string;
  offer_url: string;
  handling_time: string;
  status: string;
}

interface OfferPickerProps {
  onInsertLink: (link: string) => void;
  onClose: () => void;
  defaultAccountId?: string;
}

function isDelayed(ht: string): boolean { return !!ht && ht !== 'PT0S'; }

export default function OfferPicker({ onInsertLink, onClose, defaultAccountId }: OfferPickerProps) {
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState(defaultAccountId || '');
  const [accounts, setAccounts] = useState<any[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    axios.get(`${API}/allegro/accounts`).then(r => setAccounts(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (defaultAccountId) setAccountFilter(defaultAccountId);
  }, [defaultAccountId]);

  const fetchOffers = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params: any = { status: 'ACTIVE', limit: 50, skip: 0 };
      if (search.trim()) params.search = search.trim();
      if (accountFilter) params.account_id = accountFilter;
      const res = await axios.get(`${API}/offers`, { params });
      setOffers(res.data.items || []);
    } catch (e) {
      setOffers([]);
    }
    setLoading(false);
  };

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #d1d5db',
      borderRadius: 10,
      boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
      marginBottom: 8,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: isMobile ? 320 : 380,
      minHeight: 120,
      width: '100%',
      boxSizing: 'border-box' as const,
      overflow: 'hidden'
    }}>
      {/* Nagłówek */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '7px 10px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <span style={{ fontWeight: 600, fontSize: 12, color: '#1d4ed8' }}>🏷️ Szukaj oferty</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 16, color: '#6b7280', padding: '0 2px', lineHeight: 1 }}>✕</button>
      </div>

      {/* Wyszukiwarka */}
      <div style={{ display: 'flex', gap: 4, padding: '6px 8px', borderBottom: '1px solid #e5e7eb',
        flexShrink: 0, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') fetchOffers(); }}
          placeholder="Nazwa, SKU lub EAN..."
          style={{ flex: 1, minWidth: 80, padding: '5px 8px', border: '1px solid #d1d5db',
            borderRadius: 6, fontSize: 12, outline: 'none', boxSizing: 'border-box' as const }}
          autoFocus
        />
        <select
          value={accountFilter}
          onChange={e => setAccountFilter(e.target.value)}
          style={{ padding: '5px 4px', border: '1px solid #d1d5db', borderRadius: 6,
            fontSize: 11, background: '#fff', maxWidth: isMobile ? 90 : 130,
            flexShrink: 0 }}>
          <option value="">Wszystkie</option>
          {accounts.map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <button onClick={fetchOffers} disabled={loading}
          style={{ padding: '5px 10px', background: '#1d4ed8', color: '#fff',
            border: 'none', borderRadius: 6, fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 500, flexShrink: 0 }}>
          {loading ? '...' : 'Szukaj'}
        </button>
      </div>

      {/* Lista ofert */}
      <div style={{ overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' as any }}>
        {loading && (
          <div style={{ padding: 16, color: '#6b7280', fontSize: 12, textAlign: 'center' }}>
            Ładowanie...
          </div>
        )}
        {!loading && !searched && (
          <div style={{ padding: 16, color: '#9ca3af', fontSize: 12, textAlign: 'center' }}>
            Wpisz nazwę i kliknij Szukaj
          </div>
        )}
        {!loading && searched && offers.length === 0 && (
          <div style={{ padding: 16, color: '#9ca3af', fontSize: 12, textAlign: 'center' }}>
            Brak wyników
          </div>
        )}
        {!loading && offers.map(o => {
          const delayed = isDelayed(o.handling_time);
          return (
            <div key={o.id} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
              borderBottom: '1px solid #f3f4f6',
              background: delayed ? '#fef3c7' : '#fff'
            }}>
              {o.image_url
                ? <img src={o.image_url} alt="" style={{ width: 34, height: 34, objectFit: 'cover',
                    borderRadius: 4, flexShrink: 0, border: '1px solid #e5e7eb' }} />
                : <div style={{ width: 34, height: 34, background: '#f3f4f6', borderRadius: 4,
                    flexShrink: 0, border: '1px solid #e5e7eb' }} />
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: '#111827',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={o.name}>{o.name}</div>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
                  {o.sku && <span style={{ marginRight: 6 }}>SKU: {o.sku}</span>}
                  <span style={{ color: '#1d4ed8', fontWeight: 600 }}>
                    {o.price?.toFixed(2)} {o.currency}
                  </span>
                  {!isMobile && <span style={{ marginLeft: 6, color: '#9ca3af' }}>{o.account_name}</span>}
                </div>
              </div>
              <button
                onClick={() => onInsertLink(o.offer_url)}
                style={{ padding: isMobile ? '6px 10px' : '4px 10px',
                  background: '#1d4ed8', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                  fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap',
                  minWidth: 44, touchAction: 'manipulation' }}>
                Link
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
